import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { formatMoney, humanizeEnum } from '../../utils/format';
import {
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Box,
    TablePagination,
    Chip
} from '@mui/material';
import { StockService } from '../../services/stock.service';
import { ItemService } from '../../services/item.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { AuthService } from '../../services/auth.service';
import { Item } from '../../interfaces/item.interface';
import { Store } from '../../interfaces/store.interface';
import { StockHistory as StockHistoryModel } from '../model/stock-history';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import NotInChargeNotice from '../store/NotInChargeNotice';

/** Landed-cost rows move value, not quantity: they are labelled and valued as such. */
export const isLandedCost = (e: StockHistoryModel) => e.source === 'LANDED_COST';
export const movementLabel = (e: StockHistoryModel) => isLandedCost(e)
    ? (e.changeType === 'STOCK_OUT' ? 'Landed cost reversed' : 'Landed cost')
    : e.changeType === 'STOCK_IN' ? 'Stock In' : 'Stock Out';

/** Pre-V209 landed-cost rows carry no value: show a dash, never 0.00 / -0.00. */
export const landedCostValue = (e: StockHistoryModel) => e.valueAmount == null
    ? '\u2014'
    : `${e.changeType === 'STOCK_OUT' && e.valueAmount !== 0 ? '-' : ''}${formatMoney(e.valueAmount)}`;

const PAGE_SIZE = 10;

const StockHistory: React.FC = () => {
    const toast = useToast();
    const [items, setItems] = useState<Item[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [history, setHistory] = useState<StockHistoryModel[]>([]);
    const [selectedItemId, setSelectedItemId] = useState<string>('');
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [initialLoading, setInitialLoading] = useState<boolean>(true);
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });

    useEffect(() => {
        fetchStores();
        fetchItems();
    }, []);

    // Default view = every movement of every store you may see; store and item narrow it down on the server.
    useEffect(() => {
        fetchHistory(selectedStoreId, selectedItemId, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStoreId, selectedItemId]);

    const fetchStores = async () => {
        try {
            const response = await getStoresByInstitutionId(0, 100, 'ALL');
            setStores(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
        } finally {
            setInitialLoading(false);
        }
    };

    const fetchItems = async () => {
        try {
            const response = await ItemService.getItemsByInstitution(0, 100);
            setItems(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load items');
        }
    };

    const fetchHistory = async (storeId: string, itemId: string, page: number) => {
        setLoading(true);
        try {
            const response = await StockService.searchStockHistory(
                AuthService.getSelectedInstitution(), { storeId, itemId }, page, PAGE_SIZE);
            setHistory(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (error) {
            console.error('Error fetching stock history:', error);
            setHistory([]);
        } finally {
            setLoading(false);
        }
    };

    const handleStoreChange = (id: string) => {
        setSelectedStoreId(id);
        setPageInfo(prev => ({ ...prev, currentPage: 0 }));
    };

    const handleItemChange = (id: string) => {
        setSelectedItemId(id);
        setPageInfo(prev => ({ ...prev, currentPage: 0 }));
    };

    const handlePageChange = (_event: unknown, page: number) => {
        setPageInfo(prev => ({ ...prev, currentPage: page }));
        fetchHistory(selectedStoreId, selectedItemId, page);
    };

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy HH:mm');
    };

    if (initialLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }} data-help-page="store.history.page">
            <Typography variant="h5" sx={{ mb: 2 }}>
                Stock History
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="store.history.filter">
                <SearchableSelect<Store>
                    options={stores}
                    value={selectedStoreId}
                    onChange={handleStoreChange}
                    getOptionLabel={store => store.storeName}
                    data-help="store.history.filter.store" label="All stores"
                    size="small"
                    disabled={stores.length === 0}
                    sx={{ flex: '1 1 260px', maxWidth: 300 }}
                />

                <SearchableSelect<Item>
                    options={items}
                    value={selectedItemId}
                    onChange={handleItemChange}
                    getOptionLabel={item => item.itemName}
                    data-help="store.history.filter.item" label="All items"
                    size="small"
                    disabled={items.length === 0}
                    sx={{ flex: '1 1 260px', maxWidth: 300 }}
                />
            </Box>

            {stores.length === 0 && !loading ? (
                <NotInChargeNotice />
            ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} data-help-section="store.history.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                                    <TableCell data-help="store.history.col.no">#</TableCell>
                                    <TableCell data-help="store.history.col.date">Date</TableCell>
                                    <TableCell data-help="store.history.col.store">Store</TableCell>
                                    <TableCell data-help="store.history.col.item">Item</TableCell>
                                    <TableCell data-help="store.history.col.changeType">Change Type</TableCell>
                                    <TableCell data-help="store.history.col.amount">Amount</TableCell>
                                    <TableCell data-help="store.history.col.prevQty">Previous Qty</TableCell>
                                    <TableCell data-help="store.history.col.newQty">New Qty</TableCell>
                                    <TableCell data-help="store.history.col.source">Source</TableCell>
                                    <TableCell data-help="store.history.col.remarks">Remarks</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            No stock history found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    history.map((entry, index) => (
                                        <TableRow key={entry.id}>
                                            <TableCell>{index + 1 + pageInfo.currentPage * PAGE_SIZE}</TableCell>
                                            <TableCell>{formatDate(entry.createdAt)}</TableCell>
                                            <TableCell>{entry.storeName}</TableCell>
                                            <TableCell>{entry.itemName}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={movementLabel(entry)}
                                                    color={isLandedCost(entry) ? 'info' : entry.changeType === 'STOCK_IN' ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{isLandedCost(entry)
                                                ? landedCostValue(entry)
                                                : `${entry.amount}${entry.unitSymbol ? ` ${entry.unitSymbol}` : ''}`}</TableCell>
                                            <TableCell>{entry.previousQty}{entry.unitSymbol ? ` ${entry.unitSymbol}` : ''}</TableCell>
                                            <TableCell>{entry.newQty}{entry.unitSymbol ? ` ${entry.unitSymbol}` : ''}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={humanizeEnum(entry.source)}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{entry.remarks || '-'}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={pageInfo.totalElements}
                            page={pageInfo.currentPage}
                            onPageChange={handlePageChange}
                            rowsPerPage={PAGE_SIZE}
                            rowsPerPageOptions={[PAGE_SIZE]}
                        />
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default StockHistory;
